import { useState, useEffect } from 'react';
import { TranslateWithContext } from '../../api';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';

const shimmer = keyframes`
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
`;

const TextSkeleton = styled.span`
    display: inline-block;
    background-color: #e0e0e0;
    border-radius: 4px;
    height: 1em;
    width: 100px;
    position: relative;
    overflow: hidden;
    vertical-align: middle;
`;

const SkeletonInner = styled.span`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.5) 50%,
        rgba(255, 255, 255, 0) 100%
    );
    animation: ${shimmer} 1.5s infinite;
`;

const TranslatedTextWrapper = styled.span`
    position: relative;
    display: inline;
`;

const InfoIcon = styled.span`
    display: inline-block;
    text-transform: lowercase;
    width: 16px;
    height: 16px;
    line-height: 16px;
    text-align: center;
    background-color: #e0e0e0;
    color: #666;
    border-radius: 50%;
    font-size: 13px;
    margin-left: 4px;
    cursor: help;
    position: relative;
    vertical-align: text-top;
    
    &:hover .tooltip {
        display: block;
    }
`;

const Tooltip = styled.div`
    text-transform: none;
    display: none;
    position: absolute;
    background-color: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    left: 50%;
    transform: translateX(-50%);
    bottom: 100%;
    margin-bottom: 8px;
    width: max-content;
    max-width: 300px;
    z-index: 1000;
    font-weight: normal;
    line-height: 1.4;
    
    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: rgba(0, 0, 0, 0.9) transparent transparent transparent;
    }
`;

const DynamicallyTranslatedText = ({ text, targetLang, context = '' }) => {
    const [translatedText, setTranslatedText] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { i18n } = useTranslation('common');

    useEffect(() => {
        if (!text || !targetLang) {
            setIsLoading(false);
            return;
        }

        const translateText = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // the system uses en and fr, but the API expects 'en-US' and 'fr'
                const formattedTargetLang = targetLang === 'fr' ? 'fr' : 'en-US';
                const response = await TranslateWithContext({
                    textToTranslate: text,
                    targetLang: formattedTargetLang,
                    contextText: context
                });
                
                setTranslatedText(response.data.translatedText);
            } catch (err) {
                console.error('Translation error:', err);
                setError(err);
                setTranslatedText(text); // Fallback to original text
            } finally {
                setIsLoading(false);
            }
        };

        translateText();
    }, [text, targetLang, context]);

    if (!text || !targetLang) {
        return text || '';
    }

    if (isLoading) {
        return (
            <TextSkeleton>
                <SkeletonInner />
            </TextSkeleton>
        );
    }

    if (error) {
        console.error('Translation error, showing original text:', error);
    }

    const displayText = translatedText || text;
    // Trim both strings to avoid edge cases where only whitespace differs
    const isTranslated = translatedText && translatedText.trim() !== text.trim() && translatedText.trim() !== '';
    
    if (isTranslated) {
        return (
            <TranslatedTextWrapper>
                {displayText}
                <InfoIcon>
                    i
                    <Tooltip className="tooltip">
                        {i18n.language === "fr" 
                            ? "Traduit automatiquement" 
                            : "Automatically translated"}
                        <br />
                        <strong>{i18n.language === "fr" ? "Texte original :" : "Original text:"}</strong> {text}
                    </Tooltip>
                </InfoIcon>
            </TranslatedTextWrapper>
        );
    }
    
    return <>{displayText}</>;
};

export default DynamicallyTranslatedText;